import sharedConstants from "@lib/shared_constants";

import type {
  CdiImportKey,
  SnowflakeImportKey,
  RedshiftImportKey,
  BigQueryImportKey,
  DatabricksImportKey,
  S3ImportKey,
  FabricImportKey,
  CdiImportKeyWithId,
  BigQueryImportKeyWithId,
  DatabricksImportKeyWithId,
  RedshiftImportKeyWithId,
  S3ImportKeyWithId,
  SnowflakeImportKeyWithId,
  FabricImportKeyWithId,
} from "../integrationsCdiImport";

export type PostIntegrationsDirectCdiImportKeysGenerateProps =
  | PostIntegrationsDirectSnowflakeImportKeysGenerateProps
  | PostIntegrationsDirectRedshiftImportKeysGenerateProps
  | PostIntegrationsDirectBigQueryImportKeysGenerateProps
  | PostIntegrationsDirectDatabricksImportKeysGenerateProps
  | PostIntegrationsDirectS3ImportKeysGenerateProps
  | PostIntegrationsDirectFabricImportKeysGenerateProps;

export interface PostIntegrationsDirectBigQueryImportKeysGenerateProps {
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name;
  service_account_name: string;
  key_file: File;
  app_group_id: string;
}

export interface PostIntegrationsDirectRedshiftImportKeysGenerateProps {
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.redshift.name;
  user: string;
  password: string;
  host: string;
  port?: number;
  is_private_network?: boolean;
  ec2_host?: string;
  ssh_port?: number;
  ssh_user?: string;
  app_group_id: string;
}

export interface PostIntegrationsDirectSnowflakeImportKeysGenerateProps {
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name;
  user: string;
  snowflake_account: string;
  role: string;
  app_group_id: string;
}

export interface PostIntegrationsDirectDatabricksImportKeysGenerateProps {
  app_group_id: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.databricks.name;
  cred_name: string;
  hostname: string;
  http_path: string;
  access_token: string;
}

export interface PostIntegrationsDirectS3ImportKeysGenerateProps {
  app_group_id: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
  role_arn: string;
  external_id: string;
}

export interface PostIntegrationsDirectFabricImportKeysGenerateProps {
  app_group_id: string;
  partner_name: typeof sharedConstants.DATALAKE.PARTNER_NAME.fabric.name;
  name: string;
  tenant_id: string;
  principal_id: string;
  client_secret: string;
  connection_string: string;
  is_private_network?: boolean;
  ec2_host?: string;
  ssh_port?: number;
  ssh_user?: string;
}

export interface CreateCdiImportKeyErrors {
  error?: string;
  non_field_errors?: string[];
}

export function isCreateCdiImportKeyErrors(
  obj: CreateCdiImportKeyErrors | CdiImportKey
): obj is CreateCdiImportKeyErrors {
  return obj !== undefined && (<CreateCdiImportKeyErrors>obj).non_field_errors !== undefined;
}

export function isSnowflakeImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is SnowflakeImportKey {
  return (
    obj !== undefined &&
    (<SnowflakeImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name
  );
}

export function isSnowflakeImportKeyWithId(
  obj: CreateCdiImportKeyErrors | CdiImportKeyWithId
): obj is SnowflakeImportKeyWithId {
  return (
    obj !== undefined &&
    (<SnowflakeImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.snowflake.name &&
    (<SnowflakeImportKeyWithId>obj).id !== undefined
  );
}

export function isRedshiftImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is RedshiftImportKey {
  return (
    obj !== undefined &&
    (<RedshiftImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name
  );
}

export function isRedshiftImportKeyWithId(
  obj: CreateCdiImportKeyErrors | CdiImportKeyWithId
): obj is RedshiftImportKeyWithId {
  return (
    obj !== undefined &&
    (<RedshiftImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.redshift.name &&
    (<RedshiftImportKeyWithId>obj).id !== undefined
  );
}

export function isBigQueryImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is BigQueryImportKey {
  return (
    obj !== undefined &&
    (<BigQueryImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name
  );
}

export function isBigQueryImportKeyWithId(
  obj: CreateCdiImportKeyErrors | CdiImportKeyWithId
): obj is BigQueryImportKeyWithId {
  return (
    obj !== undefined &&
    (<BigQueryImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.bigquery.name &&
    (<BigQueryImportKeyWithId>obj).id !== undefined
  );
}

export function isDatabricksImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is DatabricksImportKey {
  return (
    obj !== undefined &&
    (<DatabricksImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name
  );
}

export function isDatabricksImportKeyWithId(
  obj: CreateCdiImportKeyErrors | CdiImportKeyWithId
): obj is DatabricksImportKeyWithId {
  return (
    obj !== undefined &&
    (<DatabricksImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.databricks.name &&
    (<DatabricksImportKeyWithId>obj).id !== undefined
  );
}

export function isS3ImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is S3ImportKey {
  return obj !== undefined && (<S3ImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.s3.name;
}

export function isS3ImportKeyWithId(obj: CreateCdiImportKeyErrors | CdiImportKeyWithId): obj is S3ImportKeyWithId {
  return (
    obj !== undefined &&
    (<S3ImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.s3.name &&
    (<S3ImportKeyWithId>obj).id !== undefined
  );
}

export function isFabricImportKey(obj: CreateCdiImportKeyErrors | CdiImportKey): obj is FabricImportKey {
  return (
    obj !== undefined && (<FabricImportKey>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name
  );
}

export function isFabricImportKeyWithId(
  obj: CreateCdiImportKeyErrors | CdiImportKeyWithId
): obj is FabricImportKeyWithId {
  return (
    obj !== undefined &&
    (<FabricImportKeyWithId>obj).partner_name === sharedConstants.DATALAKE.PARTNER_NAME.fabric.name &&
    (<FabricImportKeyWithId>obj).id !== undefined
  );
}
